import type {
  AnalyticsEventActionType,
  AnalyticsEventMetadata,
  AnalyticsEventRequestItem,
} from '@resistapp/common/api-types';

export type AnalyticsLocationSnapshot = {
  pathname: string;
  search: string;
};

const resultReviewPathPattern = /^\/(?:research|index|risk|reduction)(?:\/|$)/;
const trackedKeyboardKeys = new Set(['Enter', ' ', 'Spacebar', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

export function isResultReviewPath(pathname: string): boolean {
  return resultReviewPathPattern.test(pathname);
}

export function getLocationAnalyticsActionType(
  previousLocation: AnalyticsLocationSnapshot | null,
  nextLocation: AnalyticsLocationSnapshot,
): AnalyticsEventActionType | null {
  if (!isResultReviewPath(nextLocation.pathname)) {
    return null;
  }
  if (!previousLocation || !isResultReviewPath(previousLocation.pathname)) {
    return 'landing';
  }
  if (previousLocation.pathname !== nextLocation.pathname) {
    return 'navigation';
  }
  if (previousLocation.search !== nextLocation.search) {
    return 'query';
  }
  return null;
}

export function normaliseAnalyticsQuery(search: string): string | undefined {
  return search.length > 0 ? search : undefined;
}

export function shouldTrackAnalyticsKeydown(event: {
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
}): boolean {
  if (event.altKey || event.ctrlKey || event.metaKey) {
    return false;
  }
  return trackedKeyboardKeys.has(event.key);
}

export function buildAnalyticsEvent(params: {
  actionType: AnalyticsEventActionType;
  location: AnalyticsLocationSnapshot;
  occurredAt?: string;
  sessionId: string;
  metadata?: AnalyticsEventMetadata;
}): AnalyticsEventRequestItem {
  return {
    occurredAt: params.occurredAt ?? new Date().toISOString(),
    sessionId: params.sessionId,
    path: params.location.pathname,
    query: normaliseAnalyticsQuery(params.location.search),
    actionType: params.actionType,
    metadata: params.metadata,
  };
}
